import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useDish} from "../../../../utils/contexts/dish-context";
import {doGetTestDishes} from "../../../../logic/dish";

import OverlayContainer from "../../../UI/OverlayContainer";
import Loading from "../../../UI/Loading";
import Button from "../../../UI/Button";
import {FaChevronDown, FaChevronUp, FaTimes} from "react-icons/fa";

const DishesModal = () => {
    const {t, i18n} = useTranslation();
    const {closeDishesModal} = useDish();
    const [loading, setLoading] = useState(true);
    const [dishes, setDishes] = useState([]);
    const [expandedDishId, setExpandedDishId] = useState(null);

    const loadDishes = async () => {
        setLoading(true);
        try {
            const response = await doGetTestDishes(i18n.language);
            if (response && response.data && response.data.data) {
                setDishes(response.data.data);
            }
        } catch (error) {
            console.error("Error loading dishes:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadDishes();
    }, []);

    const toggleDish = (id) => {
        setExpandedDishId(expandedDishId === id ? null : id);
    };

    return (
        <OverlayContainer>
            <div className="bg-white rounded shadow-2xl max-h-[80vh] w-full max-w-2xl flex flex-col" style={{position: 'relative'}}>
                <div className="p-4 mb-0 font-bold uppercase text-gray-700 border-b flex justify-between items-center">
                    <div>Dishes</div>
                    <button
                        onClick={closeDishesModal}
                        className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                        title={t('Close')}
                    >
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto flex-grow">
                    {loading ? (
                        <Loading label={'Loading dishes...'}/>
                    ) : dishes.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                            {dishes.map((dish) => (
                                <div key={dish.id} className="py-2">
                                    <div
                                        className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                                        onClick={() => toggleDish(dish.id)}
                                    >
                                        <div>
                                            <div className="font-medium text-blue-600">{dish.pia_nome}</div>
                                            {dish.pia_descrizione && !expandedDishId === dish.id && (
                                                <div className="text-sm text-gray-500 truncate max-w-md">{dish.pia_descrizione}</div>
                                            )}
                                        </div>
                                        {expandedDishId === dish.id ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                                    </div>

                                    {expandedDishId === dish.id && (
                                        <div className="mt-2 p-3 bg-gray-50 rounded text-sm text-gray-700 border-l-4 border-blue-500 ml-2">
                                            {dish.pia_descrizione && (
                                                <div className="mb-2">
                                                    <span className="font-bold">Descrizione:</span> {dish.pia_descrizione}
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 gap-2 mb-2">
                                                <div><span className="font-bold">ID:</span> {dish.id}</div>
                                                <div><span className="font-bold">Gluten Free:</span> {dish.gluten_free ? 'Yes' : 'No'}</div>
                                                <div><span className="font-bold">Senza Glutine :</span> {dish.pia_senza_glutine ? 'Yes' : 'No'}</div>
                                                <div><span className="font-bold">Crudista:</span> {dish.pia_crudista ? 'Yes' : 'No'}</div>
                                            </div>

                                            {(dish.ai_missing_tokens || dish.ai_ambiguity || dish.ai_feedback) && (
                                                <div className="mb-2 p-2 bg-blue-50 rounded border border-blue-100">
                                                    <div className="font-bold text-blue-800 mb-1 italic">AI Info:</div>
                                                    {dish.ai_missing_tokens && <div><span className="font-semibold">Missing Tokens:</span> {dish.ai_missing_tokens}</div>}
                                                    {dish.ai_ambiguity && <div><span className="font-semibold">Ambiguity:</span> {dish.ai_ambiguity}</div>}
                                                    {dish.ai_feedback && <div><span className="font-semibold">Feedback:</span> {dish.ai_feedback}</div>}
                                                </div>
                                            )}

                                            {Array.isArray(dish.ingredients) && dish.ingredients.length > 0 && (
                                                <div>
                                                    <div className="font-bold border-b mb-1 pb-1">Ingredients:</div>
                                                    <ul className="list-disc ml-5 space-y-1">
                                                        {dish.ingredients.map((ing, idx) => (
                                                            <li key={idx}>
                                                                <span className="font-medium">{ing.name}</span>
                                                                <span className="text-xs text-gray-500 ml-2">
                                                                    (ID: {ing.id}, Compound: {ing.compound ? 'Yes' : 'No'}
                                                                    {ing.weight ? `, Weight: ${ing.weight}` : ''})
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="py-4 text-center text-gray-500">No dishes found.</div>
                    )}
                </div>

                <div className="p-4 border-t flex justify-end">
                    <Button
                        className="hover:underline font-medium"
                        onClick={closeDishesModal}>
                        {t('Close')}
                    </Button>
                </div>
            </div>
        </OverlayContainer>
    );
};

export default DishesModal;
